import type { TOptions } from 'i18next'
import _get from 'lodash/get'
import { P, match } from 'ts-pattern'
import { z } from 'zod'

import type { InterpolationValues, TextContent, TranslationKey } from '@flows/core/i18n/types'
import type { FlowValues } from '@flows/core/types/stepTypes'

import { i18n } from './i18n'

const toDisplayString = (value: unknown): string =>
  value === undefined || value === null ? '' : z.coerce.string().parse(value)

const i18nInterpolate = (str: string, data: Record<string, any>): string => {
  return (
    i18n?.services.interpolator.interpolate(str, data, i18n?.resolvedLanguage ?? 'en', {}) ?? ''
  )
}
export const resolveInterpolationValues = (
  configValues: InterpolationValues,
  flowValues: FlowValues
) => {
  const resolvedValues: Record<string, string> = {}
  Object.keys(configValues).forEach((valueKey) => {
    const interpolationValueConfig = configValues[valueKey]
    resolvedValues[valueKey] =
      interpolationValueConfig.type === 'staticValue'
        ? interpolationValueConfig.value
        : toDisplayString(_get(flowValues, interpolationValueConfig.path))
  })
  return resolvedValues
}

export const processStepContentElementText =
  (
    t: (key: TranslationKey, args?: TOptions) => string,
    options: { flowValues: FlowValues; servicesResponses: Record<string, unknown> }
  ) =>
  (element: TextContent) => {
    return match(element)
      .returnType<string>()
      .with({ type: 'nonTranslatableText', values: P.not(P.nullish) }, (el) =>
        i18nInterpolate(el.text, resolveInterpolationValues(el.values, options.flowValues))
      )
      .with({ type: 'nonTranslatableText' }, (el) => el.text)
      .with({ type: 'translatableText', values: P.not(P.nullish) }, (el) =>
        t(el.translationKey, resolveInterpolationValues(el.values, options.flowValues))
      )
      .with({ type: 'translatableText' }, (el) => t(el.translationKey))
      .with({ type: 'translatableTextWithDynamicKey', values: P.not(P.nullish) }, (el) =>
        t(
          el.getTranslationKey(options.flowValues),
          resolveInterpolationValues(el.values, options.flowValues)
        )
      )
      .with({ type: 'translatableTextWithDynamicKey' }, (el) =>
        t(el.getTranslationKey(options.flowValues))
      )
      .with({ type: 'nonTranslatableTextFromObject', values: P.not(P.nullish) }, (el) =>
        i18nInterpolate(
          toDisplayString(_get(options.servicesResponses, el.path)),
          resolveInterpolationValues(el.values, options.flowValues)
        )
      )
      .with({ type: 'nonTranslatableTextFromObject' }, (el) =>
        toDisplayString(_get(options.servicesResponses, el.path))
      )
      .exhaustive()
  }
